import { proxyHashInput, proxyReplyPayload } from '@secretario/shared';
import { apiFetch } from './api/http.js';

/**
 * Confere a assinatura das respostas publicadas por procuração: a fala só
 * aparece como "Secretário de fulano" quando o servidor assinou exatamente
 * aquele texto, para aquela conversa, em resposta àquela mensagem.
 */
export type ProxyTag = {
  owner: string;
  name: string;
  characterKey: string;
  ownerName: string;
  replyTo: string;
  /** Resumo (SHA-256) da mensagem respondida, como o servidor a recebeu. */
  incomingHash: string;
  kid: string;
  iat: number;
  sig: string;
};

type Verdict = 'valid' | 'invalid';

// `full`: a conferência incluiu a mensagem original (aberta neste aparelho).
const verdicts = new Map<string, { verdict: Verdict; full: boolean }>();
const running = new Set<string>();
const listeners = new Set<() => void>();
const keys = new Map<string, Promise<CryptoKey | null>>();
let keyList: Promise<{ kid: string; spki: string }[]> | undefined;

export function subscribeReplySignatures(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function readProxyTag(tag: Record<string, unknown>): ProxyTag | null {
  const text = (value: unknown, max: number) =>
    typeof value === 'string' && value.length > 0 && value.length <= max ? value : null;
  const owner = text(tag.owner, 255);
  const replyTo = text(tag.replyTo, 255);
  const kid = text(tag.kid, 64);
  const sig = text(tag.sig, 200);
  const incomingHash = text(tag.incomingHash, 64);
  if (!owner || !replyTo || !kid || !sig || !incomingHash || typeof tag.iat !== 'number') {
    return null;
  }
  return {
    owner,
    name: typeof tag.name === 'string' ? tag.name : '',
    characterKey: typeof tag.characterKey === 'string' ? tag.characterKey : '',
    ownerName: typeof tag.ownerName === 'string' ? tag.ownerName : '',
    replyTo,
    incomingHash,
    kid,
    iat: tag.iat,
    sig,
  };
}

function bytes(base64: string): Uint8Array<ArrayBuffer> {
  const normal = base64.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normal + '='.repeat((4 - (normal.length % 4)) % 4));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let index = 0; index < raw.length; index += 1) out[index] = raw.charCodeAt(index);
  return out;
}

async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

function publicKey(kid: string): Promise<CryptoKey | null> {
  const known = keys.get(kid);
  if (known) return known;
  const loading = (async () => {
    keyList ??= apiFetch<{ keys: { kid: string; spki: string }[] }>('/v1/assistant/reply-keys')
      .then((result) => result.keys)
      .catch(() => {
        keyList = undefined;
        return [];
      });
    const entry = (await keyList).find((candidate) => candidate.kid === kid);
    if (!entry) return null;
    return crypto.subtle.importKey(
      'spki',
      bytes(entry.spki),
      { name: 'ECDSA', namedCurve: 'P-256' },
      false,
      ['verify'],
    );
  })().catch(() => null);
  keys.set(kid, loading);
  // Chave desconhecida agora pode existir depois (troca de chave): tenta de novo mais tarde.
  void loading.then((key) => {
    if (!key) {
      keys.delete(kid);
      keyList = undefined;
    }
  });
  return loading;
}

export type ProxyCheck = {
  eventId: string;
  roomId: string;
  /** Quem publicou a mensagem na conversa. */
  sender: string;
  body: string;
  /** Hora do envio, em milissegundos. */
  at: number;
  /** Texto da mensagem respondida, quando este aparelho a tem aberta. */
  incoming: string | null;
  tag: ProxyTag;
};

// A resposta é publicada logo depois de assinada; muito longe disso é reuso.
const MAX_DRIFT_MS = 20 * 60 * 1000;

async function check(input: ProxyCheck): Promise<Verdict> {
  if (Math.abs(input.at - input.tag.iat * 1000) > MAX_DRIFT_MS) return 'invalid';
  // Com a mensagem original aberta aqui, ela tem de ser a mesma que o servidor respondeu.
  if (
    input.incoming !== null &&
    (await sha256Hex(proxyHashInput(input.incoming))) !== input.tag.incomingHash
  ) {
    return 'invalid';
  }
  const key = await publicKey(input.tag.kid);
  if (!key) return 'invalid';
  const valid = await crypto.subtle.verify(
    { name: 'ECDSA', hash: 'SHA-256' },
    key,
    bytes(input.tag.sig),
    new TextEncoder().encode(
      proxyReplyPayload({
        kid: input.tag.kid,
        roomId: input.roomId,
        eventId: input.tag.replyTo,
        owner: input.tag.owner,
        proxy: input.sender,
        incomingHash: input.tag.incomingHash,
        bodyHash: await sha256Hex(input.body),
        name: input.tag.name,
        characterKey: input.tag.characterKey,
        ownerName: input.tag.ownerName,
        iat: input.tag.iat,
      }),
    ),
  );
  return valid ? 'valid' : 'invalid';
}

/**
 * Resultado já conhecido, ou undefined enquanto confere (a tela é avisada
 * quando o resultado chega). Se a mensagem original só abriu depois, a
 * conferência é refeita com ela.
 */
export function proxyVerdict(input: ProxyCheck): Verdict | undefined {
  const known = verdicts.get(input.eventId);
  const full = input.incoming !== null;
  const stale = !known || (known.verdict === 'valid' && !known.full && full);
  if (stale && !running.has(input.eventId)) {
    running.add(input.eventId);
    void check(input)
      .catch(() => 'invalid' as const)
      .then((verdict) => {
        running.delete(input.eventId);
        const before = verdicts.get(input.eventId)?.verdict;
        verdicts.set(input.eventId, { verdict, full });
        if (verdicts.size > 3000) verdicts.delete(verdicts.keys().next().value as string);
        if (before !== verdict) for (const listener of listeners) listener();
      });
  }
  return known?.verdict;
}
